import { useState, useCallback, useRef } from "react";
import { Download } from "lucide-react";
import { useCompanyStaffColumns } from "./columns";
import { AVAILABLE_COLUMNS } from "./columnConfig";
import { CompanyStaffFilters } from "./CompanyStaffFilters";
import { CompanyStaffTable } from "./CompanyStaffTable";
import { useCompanyStaffPanel } from "./hooks/useCompanyStaffPanel";
import type { ProfileQueryRequest } from "@/services/api/companies";
import type { Profile } from "@/services/api/companies";

import { getActiveFiltersCount } from "./utils";
import "./companyStaffScroll.css";
import { FiltersButton } from "../shared";
import { RemoteTableLayout } from "../shared/RemoteTableLayout";
import { useRemoteTable } from "../shared/useRemoteTable";
import { Button } from "@/shared/ui/button";
import { ImportProfilesDialog } from "@/features/admin/panels/core-signal/dashboard/ImportProfilesDialog";

export function CompanyStaffView() {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const parentScrollContainerRef = useRef<HTMLDivElement>(null);

  const {
    profiles,
    totalCount,
    totalPages,
    currentPage,
    pageSize,
    allowedPageSizes,
    isLoading,
    error,
    filters,
    columnVisibility: columnVisibilityRecord,
    pagination,
    sorting,
    columnVisibilityState,
    setPagination,
    setSorting,
    setColumnVisibility,
    handlePageChange,
    handlePageSizeChange,
    setFilters,
  } = useCompanyStaffPanel();

  const columns = useCompanyStaffColumns();

  const table = useRemoteTable<Profile>({
    data: profiles,
    columns,
    pageCount: totalPages,
    defaultColumn: {
      minSize: 120,
      size: 180,
    },
    pagination,
    sorting,
    columnVisibility: columnVisibilityState,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnResizing: true,
  });

  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      setColumnVisibility(visibility);
    },
    [setColumnVisibility]
  );

  const handleFilterChange = useCallback(
    (
      key: keyof ProfileQueryRequest,
      value: string | number | boolean | string[] | undefined
    ) => {
      const newFilters = { ...filters, [key]: value };

      const normalizedFilters: Partial<ProfileQueryRequest> = {};
      Object.entries(newFilters).forEach(([k, v]) => {
        const isEmpty =
          v === undefined ||
          v === null ||
          v === "" ||
          (Array.isArray(v) && v.length === 0);
        if (!isEmpty) {
          normalizedFilters[k as keyof ProfileQueryRequest] = v as never;
        }
      });

      setFilters(normalizedFilters);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [filters, setFilters, setPagination]
  );

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [setFilters, setPagination]);

  const activeFiltersCount = getActiveFiltersCount(filters);

  return (
    <>
    <RemoteTableLayout
      table={table}
      toolbar={{
        columnVisibility: columnVisibilityRecord,
        onColumnVisibilityChange: handleColumnVisibilityChange,
        availableColumns: AVAILABLE_COLUMNS,
        leftActions: (
          <FiltersButton
            activeFiltersCount={activeFiltersCount}
            isOpen={isFiltersOpen}
            onClick={() => setIsFiltersOpen((prev) => !prev)}
          />
        ),
        actions: (
          <Button
            variant="default"
            size="sm"
            onClick={() => setImportDialogOpen(true)}
          >
            <Download className="h-4 w-4 mr-1.5" />
            Import Profiles
          </Button>
        ),
      }}
      filtersPane={
        isFiltersOpen ? (
          <CompanyStaffFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
        ) : undefined
      }
      tableContent={
        <CompanyStaffTable
          table={table}
          isLoading={isLoading}
          profiles={profiles}
          parentScrollContainerRef={parentScrollContainerRef}
        />
      }
      pagination={{
        currentPage,
        totalPages,
        pageSize,
        totalCount,
        allowedPageSizes,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        itemLabel: "Profile(s)",
      }}
      error={error}
      isLoading={isLoading}
      onScrollContainerRef={(ref: React.RefObject<HTMLDivElement | null>) => {
        if (ref.current) {
          parentScrollContainerRef.current = ref.current;
        }
      }}
    />
    <ImportProfilesDialog
      open={importDialogOpen}
      onOpenChange={setImportDialogOpen}
    />
    </>
  );
}
